import { useEffect, useState } from 'react';
import type { GroupColor, KnowledgeGroup } from '../../types';
import Modal from '@/components/base/Modal';
import { GROUP_COLORS, GROUP_COLOR_KEYS } from '../notes/groupColors';

interface KnowledgeGroupEditorModalProps {
  open: boolean;
  editing: KnowledgeGroup | null;
  saving: boolean;
  error: string;
  onClose: () => void;
  onSave: (name: string, color: GroupColor) => void;
}

export default function KnowledgeGroupEditorModal({
  open,
  editing,
  saving,
  error,
  onClose,
  onSave,
}: KnowledgeGroupEditorModalProps) {
  const [name, setName] = useState('');
  const [color, setColor] = useState<GroupColor>('primary');

  useEffect(() => {
    if (open) {
      setName(editing?.name ?? '');
      setColor(editing?.color ?? 'primary');
    }
  }, [open, editing]);

  const submit = () => {
    if (!name.trim()) return;
    onSave(name.trim(), color);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Chỉnh sửa phân loại' : 'Thêm phân loại'}
      maxWidth="max-w-sm"
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground-700">
            Tên phân loại <span className="text-accent-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="VD: Kỹ thuật, Quy trình..."
            autoFocus
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-foreground-700">
            Màu sắc
          </label>
          <div className="flex items-center gap-3">
            {GROUP_COLOR_KEYS.map((key) => {
              const style = GROUP_COLORS[key];
              const selected = color === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setColor(key)}
                  className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition ${
                    selected
                      ? 'border-foreground-300'
                      : 'border-transparent hover:border-background-300'
                  }`}
                  aria-label={`Màu ${key}`}
                >
                  <span
                    className={`flex h-7 w-7 items-center justify-center rounded-full ${style.dot}`}
                  >
                    {selected && (
                      <i className="ri-check-line text-sm text-background-50" />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
            <i className="ri-error-warning-line mt-0.5 text-base" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition hover:bg-background-100"
          >
            Hủy
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || !name.trim()}
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving && (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-background-50 border-t-transparent" />
            )}
            Lưu
          </button>
        </div>
      </div>
    </Modal>
  );
}